import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { HEALTH_LABEL } from './api';

// One lorry's tyres in the fleet list, drawn as the lorry is laid out.
//
// A row of boxes in reading order loses the only thing that tells a fitter where to walk. Grouped by
// axle, split left and right about the chassis, "the outer tyre on the rear axle" is something you
// can point at - and a whole axle running soft is visible as a shape rather than as four numbers
// that happen to be adjacent.
//
// Each tyre also carries how far it is from its own target, as a bar either side of centre. The
// difference between 104 and 105 and between 76 and 105 is obvious without anybody doing the
// arithmetic, and a tyre drifting down is visible well before it crosses a threshold.
const FILL = {
  NORMAL: 'transparent',
  WARNING: 'warning.main',
  CRITICAL: 'error.main',
  NO_DATA: 'grey.500',
  SENSOR_FAULT: 'grey.700',
};
const INK = { NORMAL: 'text.primary', WARNING: 'common.white', CRITICAL: 'common.white', NO_DATA: 'common.white', SENSOR_FAULT: 'common.white' };

const LEFT = ['LO', 'LI', 'L'];

const Wheel = ({ tyre, temperatureUnit, onSelect }) => {
  const fill = FILL[tyre.health] ?? FILL.NO_DATA;
  const ink = INK[tyre.health] ?? INK.NO_DATA;
  const target = Number(tyre.targetPressure) || 0;
  const pressure = tyre.pressure == null ? null : Number(tyre.pressure);
  // Clamped to a quarter off target either way, which is well past critical on any sane threshold.
  const off = target > 0 && pressure != null ? Math.max(-1, Math.min(1, (pressure - target) / (target * 0.25))) : 0;

  return (
    <Tooltip
      title={`${tyre.position} · ${HEALTH_LABEL[tyre.health] || tyre.health}${pressure == null ? '' : ` · ${pressure} of ${target} target`}${tyre.temperature == null ? '' : ` · ${Math.round(tyre.temperature)}°${temperatureUnit}`}`}
      arrow
    >
      <Box
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(tyre.position); } : undefined}
        onKeyDown={onSelect ? (e) => { if (e.key === 'Enter') { e.stopPropagation(); onSelect(tyre.position); } } : undefined}
        sx={{
          width: 54,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: fill,
          color: ink,
          px: 0.5,
          pt: 0.25,
          pb: 0.5,
          cursor: onSelect ? 'pointer' : 'default',
        }}
      >
        <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: 9, lineHeight: 1.1, opacity: 0.8 }}>
          {tyre.position}
        </Typography>
        <Typography component="div" fontWeight={700} sx={{ fontSize: 15, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
          {pressure == null ? '—' : Math.round(pressure)}
        </Typography>
        <Typography variant="caption" component="div" sx={{ fontSize: 9, lineHeight: 1.1, opacity: 0.8 }}>
          {tyre.temperature == null ? ' ' : `${Math.round(tyre.temperature)}°`}
        </Typography>
        {/* Distance from this tyre's own target: left of centre is under, right is over. */}
        <Box sx={{
          position: 'relative', height: 3, mt: 0.25, borderRadius: 2, bgcolor: 'action.disabledBackground',
        }}
        >
          <Box sx={{
            position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, bgcolor: 'text.disabled',
          }}
          />
          {pressure != null && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              borderRadius: 2,
              bgcolor: tyre.health === 'NORMAL' ? 'primary.main' : 'currentColor',
              left: off < 0 ? `${50 + off * 50}%` : '50%',
              width: `${Math.abs(off) * 50}%`,
            }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

const TyreStrip = ({ tyres = [], temperatureUnit = 'C', onSelect }) => {
  if (!tyres.length) return <Typography variant="body2" color="text.secondary">No readings</Typography>;
  const axles = [...new Set(tyres.map((t) => t.axleNumber))].sort((a, b) => a - b);
  const order = (t) => ['LO', 'LI', 'L', 'R', 'RI', 'RO'].indexOf(t.side);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {axles.map((axle) => {
        const on = tyres.filter((t) => t.axleNumber === axle);
        const left = on.filter((t) => LEFT.includes(t.side)).sort((a, b) => order(a) - order(b));
        const right = on.filter((t) => !LEFT.includes(t.side)).sort((a, b) => order(a) - order(b));
        return (
          <Box key={axle} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {left.map((t) => <Wheel key={t.position} tyre={t} temperatureUnit={temperatureUnit} onSelect={onSelect} />)}
            {/* The chassis between the two sides, so left and right read as left and right. */}
            <Box sx={{
              width: 18, height: 2, bgcolor: 'divider', borderRadius: 2, flexShrink: 0,
            }}
            />
            {right.map((t) => <Wheel key={t.position} tyre={t} temperatureUnit={temperatureUnit} onSelect={onSelect} />)}
          </Box>
        );
      })}
    </Box>
  );
};

export default TyreStrip;
